/**
 * Starter layouts: a house, an apartment or a set of common areas, with the
 * rooms, tasks and default counts copied in so inserting one does not depend
 * on the room library staying unchanged.
 *
 * Times are the room library's own estimates. They are editable after insert.
 */

import type { SupportedLanguage } from './languages.ts';
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } from './languages.ts';
import type { LocalizedText } from './localized-text.ts';
import type { FloorType, MeasurementBasis, RoomConditionCode, RoomSizeCode, ServiceFrequency } from './index.ts';
import {
  commercialSeedRoomTemplates,
  commercialSeedServiceTemplates,
  type CommercialSeedRoomKey,
} from './seed-templates-commercial.ts';
import {
  seedField,
  seedRoomTemplates,
  seedServiceTemplates,
  type LocalizedSeedRoomTemplate,
  type LocalizedSeedServiceTemplate,
  type SeedRoomKey,
} from './seed-templates.ts';
import {
  commercialTags,
  residentialTags,
  type RoomCategory,
  type TemplateJobType,
  type TemplateTags,
} from './template-taxonomy.ts';
import { SEED_GROUP_TRANSLATIONS_ES } from './seed-group-templates.es.ts';

export interface SeedGroupRoomSpec {
  readonly roomKey: SeedRoomKey | CommercialSeedRoomKey;
  readonly quantity?: number;
  readonly squareFeet?: number;
  /** How many fixtures a fixture-priced task in this room starts from. */
  readonly fixtureCount?: number;
  /** When set, only these task keys start selected. */
  readonly selectKeys?: readonly string[];
}

export interface SeedGroupTemplate {
  readonly key: string;
  readonly name: string;
  readonly description: string;
  readonly categoryCode: RoomCategory;
  readonly tags: TemplateTags;
  readonly notes: string;
  readonly rooms: readonly SeedGroupRoomSpec[];
}

const apt = (bedrooms: number, baths: number, half = false): SeedGroupRoomSpec[] => {
  const rooms: SeedGroupRoomSpec[] = [
    { roomKey: 'living_room', quantity: 1 },
    { roomKey: 'kitchen', quantity: 1 },
  ];
  if (bedrooms === 0) rooms.push({ roomKey: 'bedroom', quantity: 0 });
  else if (bedrooms === 1) rooms.push({ roomKey: 'bedroom', quantity: 1 });
  else {
    rooms.push({ roomKey: 'primary_bedroom', quantity: 1 });
    rooms.push({ roomKey: 'bedroom', quantity: bedrooms - 1 });
  }
  rooms.push({ roomKey: 'full_bathroom', quantity: baths });
  if (half) rooms.push({ roomKey: 'half_bathroom', quantity: 1 });
  return rooms.filter((room) => (room.quantity ?? 1) > 0);
};

const house = (bedrooms: number): SeedGroupRoomSpec[] => [
  ...apt(bedrooms, Math.max(1, Math.ceil(bedrooms / 2)), bedrooms >= 2),
  { roomKey: 'dining_room', quantity: 1 },
  { roomKey: 'laundry_room', quantity: 1 },
];

export const SEED_GROUP_SOURCE = [
  {
    key: 'studio_apartment',
    name: 'Studio apartment',
    description: 'Living space, kitchen and one bathroom.',
    categoryCode: 'living',
    tags: residentialTags(),
    notes: 'Starter layout. Counts and times are estimates you can edit.',
    rooms: apt(0, 1).filter((room) => room.roomKey !== 'bedroom'),
  },
  {
    key: 'one_bed_apartment',
    name: '1-bedroom apartment',
    description: 'One bedroom, living space, kitchen and one bathroom.',
    categoryCode: 'living',
    tags: residentialTags(),
    notes: 'Starter layout. Set the group quantity to how many identical units.',
    rooms: apt(1, 1),
  },
  {
    key: 'two_bed_apartment',
    name: '2-bedroom apartment',
    description: 'Two bedrooms, living space, kitchen and two bathrooms.',
    categoryCode: 'living',
    tags: residentialTags(),
    notes: 'Starter layout. Set the group quantity to how many identical units.',
    rooms: apt(2, 1, true),
  },
  {
    key: 'three_bed_apartment',
    name: '3-bedroom apartment',
    description: 'Three bedrooms, living space, kitchen and two bathrooms.',
    categoryCode: 'living',
    tags: residentialTags(),
    notes: 'Starter layout. A group quantity of 40 prices forty of these.',
    rooms: apt(3, 2),
  },
  {
    key: 'four_bed_apartment',
    name: '4-bedroom apartment',
    description: 'Four bedrooms, living space, kitchen and two bathrooms.',
    categoryCode: 'living',
    tags: residentialTags(),
    notes: 'Starter layout. Counts are estimates you can edit.',
    rooms: apt(4, 2, true),
  },
  {
    key: 'one_bed_house',
    name: '1-bedroom house',
    description: 'One bedroom house with living, dining, kitchen, bathroom and laundry.',
    categoryCode: 'living',
    tags: residentialTags(),
    notes: 'Starter layout. Times are editable estimates.',
    rooms: house(1),
  },
  {
    key: 'two_bed_house',
    name: '2-bedroom house',
    description: 'Two bedroom house.',
    categoryCode: 'living',
    tags: residentialTags(),
    notes: 'Starter layout. Times are editable estimates.',
    rooms: house(2),
  },
  {
    key: 'three_bed_house',
    name: '3-bedroom house',
    description: 'Three bedroom house.',
    categoryCode: 'living',
    tags: residentialTags(),
    notes: 'Starter layout. Times are editable estimates.',
    rooms: house(3),
  },
  {
    key: 'four_bed_house',
    name: '4-bedroom house',
    description: 'Four bedroom house.',
    categoryCode: 'living',
    tags: residentialTags(),
    notes: 'Starter layout. Times are editable estimates.',
    rooms: house(4),
  },
  {
    key: 'move_out',
    name: 'Move-out clean',
    description: 'Kitchen, bathrooms and the deep tasks a move-out usually adds.',
    categoryCode: 'kitchens',
    tags: residentialTags(['move_out', 'deep']),
    notes: 'Inside the oven, fridge and cabinets start selected. Turn off anything this home does not need.',
    rooms: [
      {
        roomKey: 'kitchen',
        quantity: 1,
        selectKeys: [
          'countertops',
          'cabinet_exteriors',
          'cabinet_interiors',
          'oven_interior',
          'fridge_interior',
          'sink_faucet',
          'stovetop',
          'mop_floors',
          'empty_trash',
        ],
      },
      { roomKey: 'full_bathroom', quantity: 1 },
      { roomKey: 'bedroom', quantity: 1 },
      {
        roomKey: 'living_room',
        quantity: 1,
        selectKeys: [
          'dust_surfaces',
          'vacuum_floors',
          'wipe_baseboards',
          'clean_blinds',
          'interior_windows',
          'empty_trash',
        ],
      },
    ],
  },
  {
    key: 'deep_clean_addons',
    name: 'Deep clean add-ons',
    description: 'Inside the oven, fridge and cabinets, plus baseboards and blinds.',
    categoryCode: 'kitchens',
    tags: residentialTags(['deep']),
    notes: 'These start selected. They are estimates — edit the minutes before you send the quote.',
    rooms: [
      { roomKey: 'kitchen', quantity: 1, selectKeys: ['oven_interior', 'fridge_interior', 'cabinet_interiors'] },
      { roomKey: 'living_room', quantity: 1, selectKeys: ['wipe_baseboards', 'clean_blinds'] },
    ],
  },
  {
    key: 'multifamily_common_areas',
    name: 'Multifamily common areas',
    description: 'Lobby, halls, one stair flight, elevator, laundry, mail and trash.',
    categoryCode: 'common',
    tags: commercialTags(),
    notes: 'Hall, stair, laundry, mail and trash measurements are a starting point you can edit.',
    rooms: [
      { roomKey: 'c_lobby', squareFeet: 400 },
      { roomKey: 'c_hallway_carpet', squareFeet: 800 },
      { roomKey: 'c_hallway_hard', squareFeet: 400 },
      { roomKey: 'c_stairwell', quantity: 1, squareFeet: 150 },
      { roomKey: 'c_elevator', quantity: 1 },
      { roomKey: 'c_laundry_room', quantity: 1, squareFeet: 200, fixtureCount: 8 },
      { roomKey: 'c_mailroom', squareFeet: 120 },
      { roomKey: 'c_trash_room', squareFeet: 100 },
    ],
  },
  {
    key: 'apartment_unit',
    name: 'Apartment unit',
    description: 'A 2-bedroom unit to drop into a multifamily quote beside the common areas.',
    categoryCode: 'living',
    tags: commercialTags(),
    notes: 'Set the group quantity to the number of matching units. Counts are a starting point.',
    rooms: apt(2, 1, true),
  },
] as const satisfies readonly SeedGroupTemplate[];

export type SeedGroupKey = (typeof SEED_GROUP_SOURCE)[number]['key'];

export interface SeedGroupTranslations {
  readonly groups: Readonly<
    Record<SeedGroupKey, { readonly name: string; readonly description: string; readonly notes: string }>
  >;
}

const TRANSLATIONS: Readonly<Record<Exclude<SupportedLanguage, typeof DEFAULT_LANGUAGE>, SeedGroupTranslations>> = {
  es: SEED_GROUP_TRANSLATIONS_ES,
};

/** Office hints that shipped with a starter layout, in every language the catalog has. */
function stockStarterNotes(key: string): Set<string> {
  const notes = new Set<string>();
  const source = SEED_GROUP_SOURCE.find((group) => group.key === key);
  const sourceNotes = source?.notes.trim() ?? '';
  if (sourceNotes) notes.add(sourceNotes);
  for (const translations of Object.values(TRANSLATIONS)) {
    const group = (translations.groups as Record<string, { notes?: string } | undefined>)[key];
    const text = group?.notes?.trim() ?? '';
    if (text) notes.add(text);
  }
  return notes;
}

/**
 * The note a saved layout should print under the group on a quote.
 *
 * Imported starters store an empty note: the office hint ("set the quantity
 * to 40") lives on the description. A row that still has that hint in `notes`
 * is the same thing. A note the company typed is for the customer, and the
 * layout keeping its seed key does not throw it away.
 */
export function customerNotesForSavedGroup(template: {
  seedKey?: string | null;
  notes?: string | null;
}): string | null {
  const notes = template.notes ?? null;
  const trimmed = notes?.trim() ?? '';
  if (!trimmed) return null;
  if (template.seedKey && stockStarterNotes(template.seedKey).has(trimmed)) return null;
  return notes;
}

export interface SeededGroupRoom {
  roomKey: string;
  roomTemplateId: string | null;
  roomName: string;
  roomNameTranslations: LocalizedText;
  quantity: number;
  sizeCode: RoomSizeCode;
  conditionCode: RoomConditionCode;
  flatPriceCents: null;
  measuredSquareFeet: number | null;
  floorType: FloorType | null;
  customerNotes: null;
  services: Array<{
    serviceTemplateId: string | null;
    serviceKey: string;
    name: string;
    description: string | null;
    nameTranslations: LocalizedText;
    descriptionTranslations: LocalizedText;
    estimatedMinutes: number;
    isSelected: boolean;
    showOnCustomerQuote: boolean;
    isTaxable: boolean;
    frequency: ServiceFrequency;
    measurementBasis: MeasurementBasis;
    measuredQuantity: number | null;
    productionRatePerHour: number | null;
  }>;
}

export interface LocalizedSeedGroupTemplate {
  key: string;
  name: string;
  description: string;
  notes: string;
  nameTranslations: LocalizedText;
  descriptionTranslations: LocalizedText;
  categoryCode: RoomCategory;
  tags: TemplateTags;
  jobTypes: readonly TemplateJobType[];
  rooms: SeededGroupRoom[];
}

function library(language: SupportedLanguage): {
  rooms: Map<string, LocalizedSeedRoomTemplate>;
  services: Map<string, LocalizedSeedServiceTemplate>;
} {
  const rooms = new Map<string, LocalizedSeedRoomTemplate>();
  const services = new Map<string, LocalizedSeedServiceTemplate>();
  for (const room of [...seedRoomTemplates(language), ...commercialSeedRoomTemplates(language)])
    rooms.set(room.key, room);
  for (const service of [...seedServiceTemplates(language), ...commercialSeedServiceTemplates(language)]) {
    services.set(service.key, service);
  }
  return { rooms, services };
}

export function seedGroupTemplates(language: SupportedLanguage): readonly LocalizedSeedGroupTemplate[] {
  const translations = language === DEFAULT_LANGUAGE ? null : TRANSLATIONS[language];
  const { rooms, services } = library(language);
  return SEED_GROUP_SOURCE.map((group) => {
    const text = translations?.groups[group.key];
    const name = text?.name ?? group.name;
    // Guidance stays on the template description. `notes` is printed on the
    // customer quote, so a starter hint ("set the quantity to 40") must not
    // land there.
    const guidance = (text?.notes ?? group.notes).trim();
    const baseDescription = text?.description ?? group.description;
    const description =
      guidance.length > 0 && !baseDescription.includes(guidance) ? `${baseDescription} ${guidance}` : baseDescription;
    const notes = '';
    return {
      key: group.key,
      name,
      description,
      notes,
      categoryCode: group.categoryCode,
      tags: group.tags,
      jobTypes: group.tags.jobTypes,
      nameTranslations: Object.fromEntries(
        SUPPORTED_LANGUAGES.map((lang) => [
          lang,
          lang === DEFAULT_LANGUAGE ? group.name : (TRANSLATIONS[lang].groups[group.key].name ?? group.name),
        ]),
      ) as LocalizedText,
      descriptionTranslations: Object.fromEntries(
        SUPPORTED_LANGUAGES.map((lang) => {
          const translated = lang === DEFAULT_LANGUAGE ? null : TRANSLATIONS[lang].groups[group.key];
          const base = translated?.description ?? group.description;
          const hint = (translated?.notes ?? group.notes).trim();
          return [lang, hint.length > 0 && !base.includes(hint) ? `${base} ${hint}` : base];
        }),
      ) as LocalizedText,
      rooms: group.rooms.map((spec) => {
        const room = rooms.get(spec.roomKey);
        if (!room) throw new Error(`Unknown starter room ${spec.roomKey}`);
        const quantity = seedField<number>(spec, 'quantity');
        const squareFeet = seedField<number>(spec, 'squareFeet');
        const fixtureCount = seedField<number>(spec, 'fixtureCount');
        const selectKeys = seedField<readonly string[]>(spec, 'selectKeys');
        return {
          roomKey: spec.roomKey,
          roomTemplateId: null,
          roomName: room.name,
          roomNameTranslations: room.nameTranslations,
          quantity: quantity ?? room.defaultQuantity ?? 1,
          sizeCode: 'medium' as const,
          conditionCode: 'average' as const,
          flatPriceCents: null,
          measuredSquareFeet: squareFeet ?? room.defaultSquareFeet ?? null,
          floorType: room.defaultFloorType ?? null,
          customerNotes: null,
          services: room.services.flatMap((link) => {
            const service = services.get(link.serviceKey);
            if (!service) return [];
            const selected = selectKeys ? selectKeys.includes(link.serviceKey) : link.defaultSelected;
            return [
              {
                serviceTemplateId: null,
                serviceKey: link.serviceKey,
                name: service.name,
                description: service.customerDescription,
                nameTranslations: service.nameTranslations,
                descriptionTranslations: service.customerDescriptionTranslations,
                estimatedMinutes: link.minutesOverride ?? service.defaultMinutes,
                isSelected: selected,
                showOnCustomerQuote: true,
                isTaxable: true,
                frequency: 'every_visit' as const,
                measurementBasis: service.measurementBasis ?? 'time',
                measuredQuantity:
                  link.measuredQuantity ?? (service.measurementBasis === 'fixture' ? (fixtureCount ?? null) : null),
                productionRatePerHour:
                  link.productionRateOverride ??
                  (service.measurementBasis && service.measurementBasis !== 'time'
                    ? (service.productionRatePerHour ?? null)
                    : null),
              },
            ];
          }),
        };
      }),
    };
  });
}

/**
 * Points each snapshotted task at the company's task row.
 *
 * Starter groups are built before those rows exist, so the snapshot carries
 * `serviceKey` and a null id. Persisting that null makes every inserted task
 * a custom line: use counts never move, and a later edit to the library task
 * cannot find the quote line. The same applies to the room: `roomKey` is
 * resolved to `roomTemplateId` when the caller knows the company's room rows.
 * A key with no row yet stays null and is resolved when the group is inserted.
 */
export function linkGroupServices<
  T extends {
    roomKey?: string | null;
    roomTemplateId?: string | null;
    services: ReadonlyArray<{ serviceKey?: string | null; serviceTemplateId: string | null }>;
  },
>(
  rooms: readonly T[],
  serviceIdByKey: ReadonlyMap<string, string>,
  roomIdByKey: ReadonlyMap<string, string> = new Map(),
): T[] {
  return rooms.map((room) => ({
    ...room,
    roomTemplateId: room.roomTemplateId ?? (room.roomKey ? (roomIdByKey.get(room.roomKey) ?? null) : null),
    services: room.services.map((service) => ({
      ...service,
      serviceTemplateId:
        (service.serviceKey ? serviceIdByKey.get(service.serviceKey) : undefined) ?? service.serviceTemplateId,
    })),
  }));
}
